import { ArrowDown, ArrowUp } from "lucide-react";
import { ButtonIcon, ICON_CHOICES } from "./PagePreview";

const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-3 py-2.5 text-sm text-nuit-900 placeholder:text-steel/60 focus:border-electric focus:outline-none";

// Éditeur des boutons NFC — partagé admin / espace commerçant.
// canEditLinks : URL + texte · canEditButtons : activation + ordre · canEditIcons : icônes.
export default function ButtonsEditor({ buttons, onChange, canEditLinks = true, canEditButtons = true, canEditIcons = true }) {
  const sorted = [...buttons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const update = (key, patch) =>
    onChange(sorted.map((b) => (b.key === key ? { ...b, ...patch } : b)));

  const move = (key, dir) => {
    const idx = sorted.findIndex((b) => b.key === key);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    const next = sorted.map((b, i) => {
      if (i === idx) return { ...b, order: sorted[swap].order ?? swap };
      if (i === swap) return { ...b, order: sorted[idx].order ?? idx };
      return { ...b, order: b.order ?? i };
    });
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="buttons-editor">
      {sorted.map((b, i) => (
        <div
          key={b.key}
          className={`rounded-2xl border p-4 transition-colors ${b.enabled ? "border-electric/40 bg-electric/5" : "border-nuit-900/10 bg-white"}`}
          data-testid={`btn-edit-${b.key}`}
        >
          <div className="flex items-center gap-3">
            {canEditButtons && (
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => move(b.key, -1)} disabled={i === 0} aria-label="Monter"
                  data-testid={`btn-up-${b.key}`}
                  className="rounded-md border border-nuit-900/15 p-1 text-nuit-900 disabled:opacity-25 hover:border-electric">
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => move(b.key, 1)} disabled={i === sorted.length - 1} aria-label="Descendre"
                  data-testid={`btn-down-${b.key}`}
                  className="rounded-md border border-nuit-900/15 p-1 text-nuit-900 disabled:opacity-25 hover:border-electric">
                  <ArrowDown className="h-3 w-3" />
                </button>
              </div>
            )}
            {canEditIcons ? (
              <select value={b.icon} onChange={(e) => update(b.key, { icon: e.target.value })}
                aria-label={`Icône du bouton ${b.label}`} data-testid={`btn-icon-${b.key}`}
                className="rounded-xl border border-nuit-900/15 bg-white px-2 py-2.5 text-sm text-nuit-900">
                {ICON_CHOICES.map((ic) => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-nuit-900/5 text-nuit-900">
                <ButtonIcon icon={b.icon} className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              {canEditLinks ? (
                <input className={inputCls} value={b.label} maxLength={60}
                  onChange={(e) => update(b.key, { label: e.target.value })}
                  aria-label={`Texte du bouton ${b.key}`} data-testid={`btn-label-${b.key}`} />
              ) : (
                <p className="font-display text-sm font-semibold text-nuit-900">{b.label}</p>
              )}
            </div>
            {canEditButtons && (
              <button type="button" onClick={() => update(b.key, { enabled: !b.enabled })}
                aria-pressed={b.enabled} aria-label={`Activer ${b.label}`} data-testid={`btn-toggle-${b.key}`}
                className={`h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors ${b.enabled ? "bg-electric" : "bg-nuit-900/15"}`}>
                <span className={`block h-5 w-5 rounded-full bg-white shadow transition-transform ${b.enabled ? "translate-x-5" : ""}`} />
              </button>
            )}
          </div>
          {b.key !== "vcard" && (
            <div className="mt-3">
              {canEditLinks ? (
                <input className={`${inputCls} font-mono !text-xs`} value={b.url}
                  onChange={(e) => update(b.key, { url: e.target.value })}
                  placeholder={b.key === "whatsapp" ? "https://wa.me/33612345678" : "https://…"}
                  aria-label={`URL du bouton ${b.key}`} data-testid={`btn-url-${b.key}`} />
              ) : (
                <p className="truncate font-mono text-xs text-steel">{b.url || "—"}</p>
              )}
              {b.enabled && !b.url && (
                <p className="mt-1.5 text-xs font-medium text-amber-700">
                  Bouton activé sans lien : renseignez l'URL pour qu'il apparaisse sur la page.
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
